"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth, useLogout, usePermissions } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Users,
  Shield,
  FileSearch,
  LogOut,
  Loader2,
  Package,
  PackageCheck,
  ChevronDown,
  ChevronRight,
  Home,
  Settings,
  UserCircle,
  Truck,
  Users2,
  Factory,
  FolderTree,
  Warehouse,
  ChevronsLeft,
  ChevronsRight,
  ShoppingCart,
  FileText,
  PackageOpen,
  TruckIcon,
  Send,
  Receipt,
  Box,
  LayoutDashboard,
  Route,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentUserProfile } from "@/lib/api/profile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Top-level navigation items (Suppliers & Clients)
// NOTE: Permissions use Clerk format: org:resource:action
// Note: 'manage' permission implies 'read' access
const topLevelItems = [
  {
    title: "Suppliers",
    href: "/suppliers",
    icon: Truck,
    description: "Manage suppliers and imports",
    requiredPermissions: ["org:suppliers:read"],
    // Also accepts: org:suppliers:manage, org:suppliers:write
  },
  {
    title: "Clients",
    href: "/clients",
    icon: Users2,
    description: "Manage clients and exports",
    requiredPermissions: ["org:clients:read"],
    // Also accepts: org:clients:manage, org:clients:write
  },
  {
    title: "Inventory",
    href: "/inventory/items",
    icon: Package,
    description: "Manage inventory items and stock",
    requiredPermissions: ["org:inventory:read"],
  },
  {
    title: "Sales",
    href: "/sales/dashboard",
    icon: ShoppingCart,
    description: "B2B sales, orders, packing, and deliveries",
    requiredPermissions: ["org:sales:read"],
  },
];

// Inventory sub-navigation items
const inventorySubItems = [
  {
    title: "Inventory Configs",
    href: "/inventory/configs",
    icon: Settings,
    requiredPermissions: ["org:inventory:read"],
  },
  {
    title: "Items",
    href: "/inventory/items",
    icon: Package,
    requiredPermissions: ["org:inventory:read"],
  },
  {
    title: "Stock Management",
    href: "/inventory/stock",
    icon: Warehouse,
    requiredPermissions: ["org:inventory:read"],
  },
  {
    title: "Receivings",
    href: "/receivings",
    icon: PackageCheck,
    requiredPermissions: ["org:receivings:read"],
  },
];

// Sales sub-navigation items
const salesSubItems = [
  {
    title: "Dashboard",
    href: "/sales/dashboard",
    icon: LayoutDashboard,
    requiredPermissions: ["org:sales:read"],
  },
  {
    title: "Orders",
    href: "/sales/orders",
    icon: FileText,
    requiredPermissions: ["org:sales:read"],
  },
  {
    title: "Packing",
    href: "/sales/packing",
    icon: PackageOpen,
    requiredPermissions: ["org:packing:manage"],
  },
  {
    title: "Pallets",
    href: "/sales/pallets",
    icon: Box,
    requiredPermissions: ["org:sales:read"],
  },
  {
    title: "Shipments",
    href: "/sales/shipments",
    icon: Send,
    requiredPermissions: ["org:sales:read"],
  },
  {
    title: "Invoices",
    href: "/sales/invoices",
    icon: Receipt,
    requiredPermissions: ["org:invoices:read"],
  },
  {
    title: "Trucks",
    href: "/sales/trucks",
    icon: TruckIcon,
    requiredPermissions: ["org:trucks:read"],
  },
];

// Dispatcher sub-navigation items
const dispatcherSubItems = [
  { title: "Dashboard", href: "/dispatcher", icon: LayoutDashboard, requiredPermissions: ["org:dispatcher:read"] },
  { title: "Jobs", href: "/dispatcher/jobs", icon: FileText, requiredPermissions: ["org:dispatcher:read"] },
  { title: "Routes", href: "/dispatcher/routes", icon: Route, requiredPermissions: ["org:dispatcher:read"] },
  { title: "Exceptions", href: "/dispatcher/exceptions", icon: FileSearch, requiredPermissions: ["org:dispatcher:read"] },
  { title: "Notifications", href: "/dispatcher/notifications", icon: Send, requiredPermissions: ["org:dispatcher:read"] },
  { title: "Drivers", href: "/dispatcher/drivers", icon: Truck, requiredPermissions: ["org:dispatcher:read"] },
];

// Admin sub-navigation items
const adminSubItems = [
  {
    title: "Internal User Management",
    href: "/users",
    icon: Users,
    description: "Manage internal users with Clerk integration",
    requiredPermissions: ["org:system:manage", "org:users:read"],
  },
  {
    title: "External User Management",
    href: "/external-users",
    icon: Users2,
    description: "Manage external users (customers, suppliers)",
    requiredPermissions: ["org:system:manage", "org:business:read"],
  },
  {
    title: "Roles & Permissions",
    href: "/roles",
    icon: Shield,
    description: "Manage roles and permissions",
    requiredPermissions: [
      "org:system:manage",
    ],
    // Note: org:permissions:manage and org:roles:manage imply read access
  },
  {
    title: "Warehouses",
    href: "/admin/warehouses",
    icon: Warehouse,
    description: "Manage warehouse locations",
    requiredPermissions: ["org:warehouses:manage"],
  },
  {
    title: "Detailed Logging",
    href: "/audit",
    icon: FileSearch,
    description: "View data change audit logs",
    requiredPermissions: ["org:system:manage", "org:audit_logs:read"],
  },
];

interface AdminSidebarProps {
  className?: string;
  onNavigate?: () => void;
}

export function AdminSidebar({ className, onNavigate }: AdminSidebarProps) {
  const pathname = usePathname();
  const { user, isLoading: authLoading, userId } = useAuth();
  const { logout, isLoading: logoutLoading } = useLogout();
  const {
    hasPermission,
    hasAllPermissions,
    isLoading: permissionsLoading,
  } = usePermissions();
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [dispatcherExpanded, setDispatcherExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Fetch user profile to get role
  const { data: userProfile } = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: getCurrentUserProfile,
    enabled: !!userId && !!user,
  });

  const handleNavigate = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  // Check if user has org:system:manage permission to see Admin section
  const canSeeAdmin = hasPermission("org:system:manage");
  const canSeeDispatcher = hasPermission("org:dispatcher:read");

  // Helper function to check if user has permission, allowing 'manage' or 'write' to imply 'read'
  // Also handles alternative permission mappings (e.g., org:business:read for org:suppliers:read)
  const hasPermissionOrManage = (requiredPerm: string): boolean => {
    // Check exact match first
    if (hasPermission(requiredPerm)) {
      return true
    }

    // If required permission is 'read', also check for 'manage' or 'write' (they imply read)
    if (requiredPerm.endsWith(':read')) {
      const resource = requiredPerm.replace(':read', '')
      // Check for manage permission
      if (hasPermission(`${resource}:manage`)) {
        return true
      }
      // Check for write permission
      if (hasPermission(`${resource}:write`)) {
        return true
      }
    }

    // Alternative permission mappings:
    // - org:suppliers:read can be satisfied by org:business:read (suppliers are businesses)
    // - org:clients:read can be satisfied by org:business:read (clients are businesses)
    // - org:warehouses:manage can be satisfied by org:location:manage (warehouses are locations)
    if (requiredPerm === 'org:suppliers:read' || requiredPerm === 'org:clients:read') {
      // Check for business permissions (read, write, or manage)
      if (hasPermission('org:business:read') ||
          hasPermission('org:business:write') ||
          hasPermission('org:business:manage')) {
        return true
      }
    }

    if (requiredPerm === 'org:warehouses:manage') {
      // Check for location manage permission (warehouses are locations)
      if (hasPermission('org:location:manage')) {
        return true
      }
      // Also check for location write/read as fallback
      if (hasPermission('org:location:write') || hasPermission('org:location:read')) {
        return true
      }
    }

    return false
  }

  // Helper function to check if user has all required permissions (with manage implying read)
  const hasAllRequiredPermissions = (requiredPerms: string[]): boolean => {
    return requiredPerms.every(perm => hasPermissionOrManage(perm))
  }

  // Filter top-level items based on permissions
  const visibleTopLevelItems = topLevelItems.filter((item) => {
    return hasAllRequiredPermissions(item.requiredPermissions);
  });

  // Filter inventory sub-items based on permissions
  const visibleInventoryItems = inventorySubItems.filter((item) => {
    return hasAllRequiredPermissions(item.requiredPermissions);
  });

  // Filter sales sub-items based on permissions
  const visibleSalesItems = salesSubItems.filter((item) => {
    return hasAllRequiredPermissions(item.requiredPermissions);
  });

  // Filter dispatcher sub-items based on permissions
  const visibleDispatcherItems = dispatcherSubItems.filter((item) => {
    return hasAllRequiredPermissions(item.requiredPermissions);
  });

  // Filter admin sub-items based on permissions
  const visibleAdminItems = adminSubItems.filter((item) => {
    return hasAllRequiredPermissions(item.requiredPermissions);
  });

  // Debug logging
  useEffect(() => {
    if (!permissionsLoading) {
      console.log("AdminSidebar Debug:", {
        canSeeAdmin,
        visibleAdminItemsCount: visibleAdminItems.length,
        permissionsLoading,
      });
    }
  }, [canSeeAdmin, visibleAdminItems.length, permissionsLoading]);

  // Auto-expand Dispatcher section if we're on a dispatcher page
  const isOnDispatcherPage = pathname?.startsWith("/dispatcher");

  // Auto-expand Inventory section if we're on an inventory page
  const isOnInventoryPage = pathname?.startsWith("/inventory/");

  // Auto-expand Sales section if we're on a sales page
  const isOnSalesPage = pathname?.startsWith("/sales/");

  // Auto-expand Admin section if we're on an admin page (but not suppliers/clients which are top-level)
  const isOnAdminPage =
    pathname === "/users" ||
    pathname?.startsWith("/users/") ||
    pathname === "/roles" ||
    pathname?.startsWith("/roles/") ||
    pathname === "/audit" ||
    pathname?.startsWith("/audit/");

  useEffect(() => {
    if (isOnDispatcherPage) {
      setDispatcherExpanded(true);
    }
  }, [isOnDispatcherPage]);

  useEffect(() => {
    if (isOnInventoryPage) {
      setInventoryExpanded(true);
    }
  }, [isOnInventoryPage]);

  useEffect(() => {
    if (isOnSalesPage) {
      setSalesExpanded(true);
    }
  }, [isOnSalesPage]);

  useEffect(() => {
    if (isOnAdminPage) {
      setAdminExpanded(true);
    }
  }, [isOnAdminPage]);

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border shadow-xl transition-all duration-300",
        // Subtle gradient background - darker than main content, matching theme
        "bg-gradient-to-b from-sidebar/98 via-sidebar to-sidebar/95",
        "dark:from-sidebar/99 dark:via-sidebar dark:to-sidebar/97",
        // Enhanced shadow for depth
        "shadow-[0_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)]",
        isCollapsed ? "w-20" : "w-64",
        className
      )}
    >
      {/* Logo/Branding */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <div
          className={cn(
            "flex items-center",
            isCollapsed ? "justify-center w-full" : "space-x-2"
          )}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
            <Package className="h-5 w-5 text-primary-foreground" />
          </div>
          <div
            className={cn(
              "flex flex-col transition-opacity duration-300",
              isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
            )}
          >
            <span className="text-lg font-bold text-sidebar-foreground whitespace-nowrap">
              Omizi WMS
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Portal
            </span>
          </div>
        </div>
        {!isCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-8 w-8 shrink-0"
            title="Collapse sidebar"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Expand button when collapsed - positioned at top */}
      {isCollapsed && (
        <div className="px-2 py-2 border-b border-sidebar-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-8 w-full"
            title="Expand sidebar"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Navigation Menu */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {/* Dashboard Link */}
          <Link
            href="/dashboard"
            onClick={handleNavigate}
            className={cn(
              "flex items-center rounded-md text-sm font-medium transition-colors",
              isCollapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
              pathname === "/dashboard"
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
            )}
            title={isCollapsed ? "Dashboard" : undefined}
          >
            <Home className="h-5 w-5 shrink-0" />
            <span
              className={cn(
                "transition-opacity duration-300 whitespace-nowrap",
                isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
              )}
            >
              Dashboard
            </span>
          </Link>

          {/* Top-Level Items: Suppliers, Clients, Inventory, Sales */}
          {visibleTopLevelItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              pathname === item.href || pathname?.startsWith(`${item.href}/`);
            const isInventory = item.title === "Inventory";
            const isSales = item.title === "Sales";

            // For Sales section
            if (isSales && visibleSalesItems.length > 0) {
              // When collapsed: Show sub-items directly without parent
              if (isCollapsed) {
                return (
                  <div
                    key={item.href}
                    className="space-y-1 animate-in fade-in duration-300"
                  >
                    {visibleSalesItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive =
                        pathname === subItem.href ||
                        pathname?.startsWith(`${subItem.href}/`);

                      return (
                        <Link
                          key={subItem.href}
                          href={subItem.href}
                          onClick={handleNavigate}
                          className={cn(
                            "flex items-center justify-center rounded-md px-2 py-2.5 text-sm font-medium transition-colors",
                            isSubActive
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                          )}
                          title={subItem.title}
                        >
                          <SubIcon className="h-5 w-5 shrink-0" />
                        </Link>
                      );
                    })}
                  </div>
                );
              }

              // When expanded: Show as expandable parent with sub-items
              return (
                <div key={item.href} className="space-y-1">
                  <button
                    onClick={() => setSalesExpanded(!salesExpanded)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                    )}
                    title={item.description}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="flex-1 text-left transition-opacity duration-300 whitespace-nowrap">
                      {item.title}
                    </span>
                    {salesExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    )}
                  </button>

                  {/* Sales Sub-Items (Collapsible) */}
                  {salesExpanded && (
                    <div className="ml-4 space-y-1 border-l-2 border-border pl-2">
                      {visibleSalesItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive =
                          pathname === subItem.href ||
                          pathname?.startsWith(`${subItem.href}/`);

                        return (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            onClick={handleNavigate}
                            className={cn(
                              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                              isSubActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                            )}
                          >
                            <SubIcon className="h-4 w-4 shrink-0" />
                            <span className="text-xs whitespace-nowrap">
                              {subItem.title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // For Inventory section
            if (isInventory && visibleInventoryItems.length > 0) {
              // When collapsed: Show sub-items directly without parent
              if (isCollapsed) {
                return (
                  <div
                    key={item.href}
                    className="space-y-1 animate-in fade-in duration-300"
                  >
                    {visibleInventoryItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive =
                        pathname === subItem.href ||
                        pathname?.startsWith(`${subItem.href}/`);

                      return (
                        <Link
                          key={subItem.href}
                          href={subItem.href}
                          onClick={handleNavigate}
                          className={cn(
                            "flex items-center justify-center rounded-md px-2 py-2.5 text-sm font-medium transition-colors",
                            isSubActive
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                          )}
                          title={subItem.title}
                        >
                          <SubIcon className="h-5 w-5 shrink-0" />
                        </Link>
                      );
                    })}
                  </div>
                );
              }

              // When expanded: Show as expandable parent with sub-items
              return (
                <div key={item.href} className="space-y-1">
                  <button
                    onClick={() => setInventoryExpanded(!inventoryExpanded)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                    )}
                    title={item.description}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="flex-1 text-left transition-opacity duration-300 whitespace-nowrap">
                      {item.title}
                    </span>
                    {inventoryExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    )}
                  </button>

                  {/* Inventory Sub-Items (Collapsible) */}
                  {inventoryExpanded && (
                    <div className="ml-4 space-y-1 border-l-2 border-border pl-2">
                      {visibleInventoryItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive =
                          pathname === subItem.href ||
                          pathname?.startsWith(`${subItem.href}/`);

                        return (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            onClick={handleNavigate}
                            className={cn(
                              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                              isSubActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                            )}
                          >
                            <SubIcon className="h-4 w-4 shrink-0" />
                            <span className="text-xs whitespace-nowrap">
                              {subItem.title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular top-level item (Suppliers, Clients)
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={handleNavigate}
                className={cn(
                  "flex items-center rounded-md text-sm font-medium transition-colors",
                  isCollapsed
                    ? "justify-center px-2 py-2.5"
                    : "gap-3 px-3 py-2.5",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                )}
                title={isCollapsed ? item.title : item.description}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span
                  className={cn(
                    "transition-opacity duration-300 whitespace-nowrap",
                    isCollapsed
                      ? "opacity-0 w-0 overflow-hidden"
                      : "opacity-100"
                  )}
                >
                  {item.title}
                </span>
              </Link>
            );
          })}

          {/* Dispatcher Section (Expandable) */}
          {canSeeDispatcher && visibleDispatcherItems.length > 0 && (
            <div className="space-y-1">
              {/* When collapsed: Show sub-items directly without parent */}
              {isCollapsed ? (
                <div className="space-y-1 animate-in fade-in duration-300">
                  {visibleDispatcherItems.map((item) => {
                    const Icon = item.icon;
                    const isActive =
                      pathname === item.href ||
                      pathname?.startsWith(`${item.href}/`);

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={handleNavigate}
                        className={cn(
                          "flex items-center justify-center rounded-md px-2 py-2.5 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                        )}
                        title={item.title}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1 animate-in fade-in duration-300">
                  {/* Dispatcher Header (Expandable) */}
                  <button
                    onClick={() => setDispatcherExpanded(!dispatcherExpanded)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isOnDispatcherPage
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                    )}
                  >
                    <Route className="h-5 w-5 shrink-0" />
                    <span className="flex-1 text-left transition-opacity duration-300 whitespace-nowrap">
                      Dispatcher
                    </span>
                    {dispatcherExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    )}
                  </button>

                  {/* Dispatcher Sub-Items (Collapsible) */}
                  {dispatcherExpanded && (
                    <div className="ml-4 space-y-1 border-l-2 border-border pl-2">
                      {visibleDispatcherItems.map((item) => {
                        const Icon = item.icon;
                        const isActive =
                          pathname === item.href ||
                          pathname?.startsWith(`${item.href}/`);

                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={handleNavigate}
                            className={cn(
                              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span className="text-xs whitespace-nowrap">
                              {item.title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Admin Settings Section (Expandable) */}
          {canSeeAdmin && visibleAdminItems.length > 0 && (
            <div className="space-y-1">
              {/* When collapsed: Show sub-items directly without parent */}
              {isCollapsed ? (
                <div className="space-y-1 animate-in fade-in duration-300">
                  {visibleAdminItems.map((item) => {
                    const Icon = item.icon;
                    const isActive =
                      pathname === item.href ||
                      pathname?.startsWith(`${item.href}/`);

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={handleNavigate}
                        className={cn(
                          "flex items-center justify-center rounded-md px-2 py-2.5 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                        )}
                        title={item.title}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1 animate-in fade-in duration-300">
                  {/* Admin Settings Header (Expandable) */}
                  <button
                    onClick={() => setAdminExpanded(!adminExpanded)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      isOnAdminPage
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                    )}
                  >
                    <Shield className="h-5 w-5 shrink-0" />
                    <span className="flex-1 text-left transition-opacity duration-300 whitespace-nowrap">
                      Admin Settings
                    </span>
                    {adminExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 transition-opacity duration-300" />
                    )}
                  </button>

                  {/* Admin Settings Sub-Items (Collapsible) */}
                  {adminExpanded && (
                    <div className="ml-4 space-y-1 border-l-2 border-border pl-2">
                      {/* Admin Sub-Items */}
                      {visibleAdminItems.map((item) => {
                        const Icon = item.icon;
                        const isActive =
                          pathname === item.href ||
                          pathname?.startsWith(`${item.href}/`);

                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={handleNavigate}
                            className={cn(
                              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-primary hover:bg-sidebar-accent"
                            )}
                            title={item.description}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span className="text-xs whitespace-nowrap">
                              {item.title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {permissionsLoading && (
            <div
              className={cn(
                "flex items-center px-3 py-2",
                isCollapsed ? "justify-center" : "space-x-3"
              )}
            >
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span
                className={cn(
                  "text-sm text-muted-foreground transition-opacity duration-300 whitespace-nowrap",
                  isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
                )}
              >
                Loading navigation...
              </span>
            </div>
          )}
        </nav>
      </ScrollArea>

      <Separator />

      {/* User Profile Section */}
      <div
        className={cn(
          "border-t border-sidebar-border",
          isCollapsed ? "p-2" : "p-4"
        )}
      >
        {authLoading ? (
          <div
            className={cn(
              "flex items-center px-3 py-2",
              isCollapsed ? "justify-center" : "space-x-3"
            )}
          >
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span
              className={cn(
                "text-sm text-muted-foreground transition-opacity duration-300 whitespace-nowrap",
                isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
              )}
            >
              Loading...
            </span>
          </div>
        ) : user ? (
          <div className={cn(isCollapsed ? "space-y-2" : "space-y-3")}>
            {/* User Info - Clickable to navigate to Profile Settings */}
            <Link
              href="/profile"
              onClick={handleNavigate}
              className={cn(
                "flex items-center rounded-md bg-sidebar-accent hover:bg-muted transition-colors cursor-pointer",
                isCollapsed ? "justify-center p-2" : "space-x-3 px-3 py-2"
              )}
              title={
                isCollapsed
                  ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Admin User"
                  : undefined
              }
            >
              {/* Profile Picture from Clerk */}
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={user.imageUrl || undefined} alt="Profile" />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                  {user.firstName?.[0] ||
                    user.emailAddresses?.[0]?.emailAddress?.[0] ||
                    "A"}
                  {user.lastName?.[0] || ""}
                </AvatarFallback>
              </Avatar>
              <div
                className={cn(
                  "flex-1 min-w-0 transition-opacity duration-300",
                  isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
                )}
              >
                <p className="text-sm font-medium text-sidebar-foreground truncate whitespace-nowrap">
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "Admin User"}
                </p>
                <p className="text-xs text-muted-foreground truncate whitespace-nowrap capitalize">
                  {userProfile?.role || "User"}
                </p>
              </div>
            </Link>

            {/* Sign Out Button */}
            <Button
              variant="outline"
              className={cn(
                "w-full",
                isCollapsed ? "justify-center px-0" : "justify-start"
              )}
              onClick={() => logout()}
              disabled={logoutLoading}
              title={isCollapsed ? "Sign Out" : undefined}
            >
              {logoutLoading ? (
                <Loader2
                  className={cn("h-4 w-4 animate-spin", !isCollapsed && "mr-2")}
                />
              ) : (
                <LogOut className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
              )}
              <span
                className={cn(
                  "transition-opacity duration-300 whitespace-nowrap",
                  isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
                )}
              >
                Sign Out
              </span>
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Link href="/sign-in">
              <Button
                variant="default"
                className={cn(
                  "w-full",
                  isCollapsed ? "px-2 justify-center" : ""
                )}
              >
                {isCollapsed ? (
                  <UserCircle className="h-4 w-4" />
                ) : (
                  <span className="transition-opacity duration-300 whitespace-nowrap">
                    Sign In
                  </span>
                )}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
